/**
 * OpenPAYGO Exceptions — dual-control recovery approvals.
 * Separate from Payment Approvals (cash → days) and Key Approvals (first install token).
 * Actions: counter_sync | set_token_count | full_reset. Requester ≠ approver.
 *
 * Credentials (secret key / starting code) are NEVER typed here — approve loads them
 * from the selected tenant Device row. The form only picks the device + action.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { Box, Typography } from "@mui/material";
import { AppBadge, AppButton, AppCard, AppField, AppInput, AppSelect, ConfirmAction } from "../../../shared/components/ui";
import { AppDataTable } from "../../../shared/components/AppDataTable";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import { ApiError, api } from "../../../shared/api/client";

type ExceptionRow = {
  id: number;
  serial_number: string;
  action: string;
  status: string;
  reason: string;
  payload?: Record<string, unknown> | null;
  requested_by_id: number;
  requested_by_username: string | null;
  requested_at: string | null;
  decision_note?: string | null;
  result_payload?: Record<string, unknown> | null;
  /** Server-computed affordances for the current viewer — drives button visibility. */
  can_approve: boolean;
  can_reject: boolean;
  can_cancel: boolean;
};

type DeviceOption = {
  id: number;
  serial_number: string;
  is_paygo?: boolean;
  token_count?: number;
  starting_code?: number | null;
  customer_name?: string | null;
  customer_number?: string | null;
};

type DeviceContext = {
  serial_number: string;
  token_count: number;
  starting_code: number | null;
  secret_key_masked: string | null;
  customer_name?: string | null;
  customer_number?: string | null;
  is_paygo?: boolean;
};

type DeviceDetailResponse = {
  device: {
    serial_number: string;
    token_count: number;
    starting_code: number | null;
    secret_key_masked: string | null;
    is_paygo?: boolean;
    customer?: { name?: string; customer_number?: string } | null;
  };
};

const ACTIONS = [
  { value: "counter_sync", label: "Counter sync (issue sync token)" },
  { value: "set_token_count", label: "Set server token count" },
  { value: "full_reset", label: "Full PAYG server reset" },
];

function actionLabel(action: string): string {
  return ACTIONS.find((a) => a.value === action)?.label ?? action.replace(/_/g, " ");
}

function friendlyError(ex: unknown): string {
  if (ex instanceof ApiError) {
    const hint = typeof ex.details?.hint === "string" ? ex.details.hint : null;
    return hint || ex.message;
  }
  return ex instanceof Error ? ex.message : "Something went wrong";
}

export function OpenPaygoExceptionsQueue() {
  const [searchParams] = useSearchParams();
  const serialPrefill = searchParams.get("serial") || "";

  const [rows, setRows] = useState<ExceptionRow[]>([]);
  const [pageState, setPageState] = useState<"loading" | "error" | "success">("loading");
  const [pageError, setPageError] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState("");
  const [issuedToken, setIssuedToken] = useState<{ id: number; token: string; hint?: string } | null>(null);
  const [approveSuccess, setApproveSuccess] = useState<{ id: number; message: string } | null>(null);

  const [devices, setDevices] = useState<DeviceOption[]>([]);
  const [deviceFilter, setDeviceFilter] = useState("");
  const [serial, setSerial] = useState(serialPrefill);
  const [deviceCtx, setDeviceCtx] = useState<DeviceContext | null>(null);
  const [deviceCtxBusy, setDeviceCtxBusy] = useState(false);
  const [deviceCtxError, setDeviceCtxError] = useState("");
  const [action, setAction] = useState("counter_sync");
  const [reason, setReason] = useState("");
  const [tokenCount, setTokenCount] = useState("");
  const [submitBusy, setSubmitBusy] = useState(false);
  const [submitMsg, setSubmitMsg] = useState("");
  const [confirmApprove, setConfirmApprove] = useState<ExceptionRow | null>(null);

  useEffect(() => {
    if (serialPrefill) setSerial(serialPrefill);
  }, [serialPrefill]);

  const load = useCallback(async () => {
    setPageState("loading");
    setPageError("");
    try {
      const data = await api.get<{ exceptions: ExceptionRow[] }>(
        "/openpaygo-exceptions?status=pending&per_page=100",
      );
      setRows(data.exceptions ?? []);
      setPageState("success");
    } catch (ex: unknown) {
      setPageError(friendlyError(ex));
      setPageState("error");
    }
  }, []);

  const loadDevices = useCallback(async () => {
    try {
      const params = new URLSearchParams({ per_page: "200" });
      const q = deviceFilter.trim();
      if (q) params.set("search", q);
      const data = await api.get<{ devices: DeviceOption[] }>(`/devices?${params}`);
      const list = (data.devices ?? []).filter((d) => d.is_paygo !== false);
      setDevices(list);
    } catch {
      setDevices([]);
    }
  }, [deviceFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => void loadDevices(), deviceFilter ? 250 : 0);
    return () => clearTimeout(t);
  }, [loadDevices, deviceFilter]);

  const loadDeviceContext = useCallback(async (serialNumber: string) => {
    const sn = serialNumber.trim();
    if (!sn) {
      setDeviceCtx(null);
      setDeviceCtxError("");
      return;
    }
    setDeviceCtxBusy(true);
    setDeviceCtxError("");
    try {
      const data = await api.get<DeviceDetailResponse>(`/devices/${encodeURIComponent(sn)}`);
      const d = data.device;
      if (!d) throw new Error("Device not found in your company.");
      if (d.is_paygo === false) {
        throw new Error("Selected device is not OpenPAYGO-enabled.");
      }
      setDeviceCtx({
        serial_number: d.serial_number,
        token_count: d.token_count,
        starting_code: d.starting_code ?? null,
        secret_key_masked: d.secret_key_masked ?? null,
        customer_name: d.customer?.name ?? null,
        customer_number: d.customer?.customer_number ?? null,
        is_paygo: d.is_paygo,
      });
      // Prefill set/reset count from current server counter so staff start from inventory truth.
      setTokenCount(String(d.token_count ?? ""));
    } catch (ex: unknown) {
      setDeviceCtx(null);
      setDeviceCtxError(friendlyError(ex));
    } finally {
      setDeviceCtxBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!serial.trim()) {
      setDeviceCtx(null);
      setDeviceCtxError("");
      return;
    }
    const t = setTimeout(() => void loadDeviceContext(serial), 200);
    return () => clearTimeout(t);
  }, [serial, loadDeviceContext]);

  const filteredDeviceOptions = useMemo(() => {
    const q = deviceFilter.trim().toLowerCase();
    if (!q) return devices;
    return devices.filter((d) => {
      const hay = `${d.serial_number} ${d.customer_name ?? ""} ${d.customer_number ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [devices, deviceFilter]);

  const submitRequest = async () => {
    setActionError("");
    setSubmitMsg("");
    setSubmitBusy(true);
    try {
      if (!serial.trim()) throw new Error("Select a device from your company inventory.");
      if (!deviceCtx) throw new Error("Wait for device credentials to load, or pick another serial.");
      const payload: Record<string, unknown> = {};
      if (action === "set_token_count") {
        const n = Number(tokenCount);
        if (!Number.isFinite(n) || n < 0) {
          throw new Error("Enter a valid token_count (>= 0).");
        }
        payload.token_count = n;
      }
      if (action === "full_reset" && tokenCount.trim() !== "") {
        payload.token_count = Number(tokenCount);
        payload.relock = true;
      }
      await api.post("/openpaygo-exceptions", {
        serial_number: serial.trim(),
        action,
        reason: reason.trim(),
        payload,
      });
      setSubmitMsg("Request submitted — another Business Admin or Commercial Officer must approve.");
      setReason("");
      await load();
    } catch (ex: unknown) {
      setActionError(friendlyError(ex));
    } finally {
      setSubmitBusy(false);
    }
  };

  const approve = async (row: ExceptionRow) => {
    setBusyId(row.id);
    setActionError("");
    setIssuedToken(null);
    setApproveSuccess(null);
    try {
      const res = await api.post<{
        token?: string;
        entry_hint?: string;
        message?: string;
        result?: Record<string, unknown>;
      }>(`/openpaygo-exceptions/${row.id}/approve`, {});
      if (res.token) {
        setIssuedToken({ id: row.id, token: res.token, hint: res.entry_hint });
      } else {
        // set_token_count / full_reset — no token issued; show message confirmation.
        const msg =
          res.message ??
          (typeof res.result?.message === "string" ? res.result.message : null) ??
          `Exception #${row.id} approved successfully.`;
        setApproveSuccess({ id: row.id, message: msg });
      }
      await load();
    } catch (ex: unknown) {
      setActionError(friendlyError(ex));
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (row: ExceptionRow) => {
    setBusyId(row.id);
    setActionError("");
    try {
      await api.post(`/openpaygo-exceptions/${row.id}/reject`, { note: "Rejected from exceptions queue" });
      await load();
    } catch (ex: unknown) {
      setActionError(friendlyError(ex));
    } finally {
      setBusyId(null);
    }
  };

  const cancelOwn = async (row: ExceptionRow) => {
    setBusyId(row.id);
    setActionError("");
    try {
      await api.post(`/openpaygo-exceptions/${row.id}/cancel`, { note: "Withdrawn by requester" });
      await load();
    } catch (ex: unknown) {
      setActionError(friendlyError(ex));
    } finally {
      setBusyId(null);
    }
  };

  const columns: ColumnDef<ExceptionRow, unknown>[] = useMemo(
    () => [
      { accessorKey: "id", header: "ID" },
      { accessorKey: "serial_number", header: "Device" },
      {
        id: "action",
        header: "Action",
        cell: ({ row }) => <AppBadge tone="warning">{actionLabel(row.original.action)}</AppBadge>,
      },
      {
        id: "reason",
        header: "Reason",
        accessorFn: (r) => r.reason,
      },
      {
        id: "requested_by",
        header: "Requested by",
        accessorFn: (r) => r.requested_by_username ?? `#${r.requested_by_id}`,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => {
          const r = row.original;
          const busy = busyId === r.id;
          const hasAny = r.can_approve || r.can_reject || r.can_cancel;
          if (!hasAny) {
            return (
              <Typography variant="caption" color="text.secondary">
                {r.status === "pending" ? "Awaiting another approver" : r.status}
              </Typography>
            );
          }
          return (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              {r.can_approve && (
                <AppButton type="button" disabled={busy} onClick={() => setConfirmApprove(r)}>
                  {busy ? "…" : "Approve"}
                </AppButton>
              )}
              {r.can_reject && (
                <AppButton type="button" variant="secondary" disabled={busy} onClick={() => void reject(r)}>
                  Reject
                </AppButton>
              )}
              {r.can_cancel && (
                <AppButton type="button" variant="secondary" disabled={busy} onClick={() => void cancelOwn(r)}>
                  {r.can_approve ? "Cancel" : "Withdraw"}
                </AppButton>
              )}
            </Box>
          );
        },
      },
    ],
    [busyId],
  );

  const listStatus =
    pageState === "success" && rows.length === 0 ? "empty" : pageState;

  return (
    <div>
      <PageHeader
        title="OpenPAYGO Exceptions"
        subtitle="Dual-control recovery: pick a company device (credentials load from inventory — never re-type secret key). Requester Withdraw; another BA/CO Approve / Reject."
      />

      <AppCard title="Submit exception request" style={{ marginBottom: 16 }}>
        <Box sx={{ display: "grid", gap: 1.5, maxWidth: 560 }}>
          <AppField label="Filter devices (serial / customer)">
            <AppInput
              value={deviceFilter}
              onChange={(e) => setDeviceFilter(e.target.value)}
              placeholder="Type to search your tenant inventory…"
              disabled={submitBusy}
            />
          </AppField>
          <AppField label="Device (from your company)">
            <AppSelect
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              disabled={submitBusy}
            >
              <option value="">Select a PayGO device…</option>
              {serial && !filteredDeviceOptions.some((d) => d.serial_number === serial) ? (
                <option value={serial}>{serial} (from link / lookup)</option>
              ) : null}
              {filteredDeviceOptions.map((d) => (
                <option key={d.id} value={d.serial_number}>
                  {d.serial_number}
                  {d.customer_name ? ` — ${d.customer_name}` : " — unassigned"}
                  {typeof d.token_count === "number" ? ` (count ${d.token_count})` : ""}
                </option>
              ))}
            </AppSelect>
          </AppField>

          {deviceCtxBusy ? (
            <Typography variant="body2" color="text.secondary">Loading device credentials…</Typography>
          ) : null}
          {deviceCtxError ? (
            <Typography variant="body2" color="error" role="alert">{deviceCtxError}</Typography>
          ) : null}
          {deviceCtx ? (
            <Box
              sx={{
                p: 1.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "action.hover",
                display: "grid",
                gap: 0.75,
              }}
            >
              <Typography variant="subtitle2">Used on approve (from device record)</Typography>
              <Typography variant="body2">
                Serial: <strong>{deviceCtx.serial_number}</strong>
                {deviceCtx.customer_name
                  ? ` · ${deviceCtx.customer_name}${deviceCtx.customer_number ? ` (${deviceCtx.customer_number})` : ""}`
                  : " · unassigned stock"}
              </Typography>
              <Typography variant="body2">
                Server token_count: <strong>{deviceCtx.token_count}</strong>
              </Typography>
              <Typography variant="body2">
                Starting code: <strong>{deviceCtx.starting_code ?? "—"}</strong>
              </Typography>
              <Typography variant="body2">
                Secret key: <strong>{deviceCtx.secret_key_masked ?? "—"}</strong>
                {" "}
                <Typography component="span" variant="caption" color="text.secondary">
                  (masked — full key stays on server)
                </Typography>
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Counter sync / tokens use these stored credentials. Wrong key? Fix on{" "}
                <Link to={`/devices/${encodeURIComponent(deviceCtx.serial_number)}`}>Device Detail</Link>
                , then return here.
              </Typography>
            </Box>
          ) : null}

          <AppField label="Action">
            <AppSelect value={action} onChange={(e) => setAction(e.target.value)} disabled={submitBusy}>
              {ACTIONS.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </AppSelect>
          </AppField>
          {(action === "set_token_count" || action === "full_reset") && (
            <AppField
              label={
                action === "set_token_count"
                  ? "New token_count (prefilled from device)"
                  : "Reset token_count (optional; blank = 1)"
              }
            >
              <AppInput
                type="number"
                min={0}
                value={tokenCount}
                onChange={(e) => setTokenCount(e.target.value)}
                placeholder={action === "set_token_count" ? "e.g. 47" : "1"}
                disabled={submitBusy}
              />
            </AppField>
          )}
          <AppField label="Reason (min 10 characters)">
            <AppInput
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why is this recovery needed? Cite hardware/CSV evidence."
              disabled={submitBusy}
            />
          </AppField>
          <AppButton
            type="button"
            onClick={() => void submitRequest()}
            disabled={submitBusy || !serial.trim() || !deviceCtx || reason.trim().length < 10}
          >
            {submitBusy ? "Submitting…" : "Submit for approval"}
          </AppButton>
          {submitMsg ? <Typography variant="body2" color="success.main">{submitMsg}</Typography> : null}
        </Box>
      </AppCard>

      {actionError ? (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {actionError}
        </Typography>
      ) : null}

      {issuedToken ? (
        <AppCard title="Token issued (show once)" style={{ marginBottom: 16 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Exception #{issuedToken.id} approved. Enter on device:
          </Typography>
          <Box
            component="code"
            sx={{ display: "block", p: 1.5, bgcolor: "action.hover", fontSize: "1.25rem", letterSpacing: 2 }}
          >
            {issuedToken.token}
          </Box>
          {issuedToken.hint ? (
            <Typography variant="caption" color="text.secondary">
              {issuedToken.hint}
            </Typography>
          ) : null}
          <AppButton
            type="button"
            variant="secondary"
            style={{ marginTop: 8 }}
            onClick={() => navigator.clipboard.writeText(issuedToken.token)}
          >
            Copy token
          </AppButton>
        </AppCard>
      ) : null}

      {approveSuccess ? (
        <AppCard title="Exception approved" style={{ marginBottom: 16 }}>
          <Typography variant="body2" color="success.main">
            {approveSuccess.message}
          </Typography>
          <AppButton
            type="button"
            variant="secondary"
            style={{ marginTop: 8 }}
            onClick={() => setApproveSuccess(null)}
          >
            Dismiss
          </AppButton>
        </AppCard>
      ) : null}

      <PageState
        status={listStatus}
        error={pageError}
        onRetry={load}
        emptyTitle="No pending OpenPAYGO exceptions"
        emptyHint="Counter sync and other recovery tools appear here after a manager requests them."
      >
        <AppDataTable columns={columns} data={rows} />
      </PageState>

      <ConfirmAction
        open={!!confirmApprove}
        title="Approve OpenPAYGO exception?"
        message={
          confirmApprove
            ? `Approve ${actionLabel(confirmApprove.action)} for ${confirmApprove.serial_number}? This executes recovery immediately.`
            : ""
        }
        confirmLabel="Approve"
        danger
        busy={busyId === confirmApprove?.id}
        onCancel={() => setConfirmApprove(null)}
        onConfirm={() => {
          const row = confirmApprove;
          setConfirmApprove(null);
          if (row) void approve(row);
        }}
      />
    </div>
  );
}
